import apiClient from '@/lib/apiClient';
import { ApiResponse } from '@/types/api';

export interface Tenant {
  id: string;
  company_name: string;
  subdomain: string;
  domain?: string;
  subscription_tier: 'starter' | 'professional' | 'enterprise' | 'custom';
  subscription_status: 'trial' | 'active' | 'suspended' | 'cancelled';
  is_active: boolean;
  trial_ends_at?: string;
  subscription_started_at?: string;
  subscription_ends_at?: string;
  created_at: string;
  updated_at: string;
  settings?: Record<string, any>;
  limits?: Record<string, any>;
  usage?: Record<string, any>;
}

export interface CreateTenantRequest {
  name: string;
  subdomain: string;
  domain?: string;
  subscription_tier?: string;
  subscription_status?: string;
  settings?: Record<string, any>;
  trial_days?: number;
}

export interface UpdateTenantRequest {
  name?: string;
  company_name?: string;
  domain?: string;
  custom_domain?: string;
  is_active?: boolean;
  settings?: Record<string, any>;
  subscription_tier?: string;
  subscription_status?: string;
  limits?: Record<string, any>;
}

export interface TenantStats {
  total: number;
  active: number;
  suspended: number;
  trial: number;
}

/**
 * Tenant API endpoints
 */
export const tenantApi = {
  /**
   * Get all tenants (admin only) with pagination and filters
   */
  getTenants: async (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    status?: string;
    tier?: string;
  }): Promise<{
    tenants: Tenant[];
    total: number;
    pages: number;
    current_page: number;
    per_page: number;
  }> => {
    const response = await apiClient.get<
      ApiResponse<{
        tenants: Tenant[];
        total: number;
        pages: number;
        current_page: number;
        per_page: number;
      }>
    >('/tenants/admin/tenants', {
      params,
    });
    return response.data.data!;
  },

  /**
   * Get single tenant by ID (admin only)
   */
  getTenant: async (id: string): Promise<Tenant> => {
    const response = await apiClient.get<ApiResponse<Tenant>>(
      `/tenants/admin/tenants/${id}`
    );
    return response.data.data!;
  },

  /**
   * Create new tenant (admin only)
   */
  createTenant: async (data: CreateTenantRequest): Promise<Tenant> => {
    const response = await apiClient.post<ApiResponse<Tenant>>(
      '/tenants/admin/tenants',
      data
    );
    return response.data.data!;
  },

  /**
   * Update existing tenant (admin only)
   */
  updateTenant: async (
    id: string,
    data: UpdateTenantRequest
  ): Promise<Tenant> => {
    const response = await apiClient.patch<ApiResponse<Tenant>>(
      `/tenants/admin/tenants/${id}`,
      data
    );
    return response.data.data!;
  },

  /**
   * Suspend tenant (admin only)
   */
  suspendTenant: async (id: string, reason?: string): Promise<Tenant> => {
    const response = await apiClient.post<ApiResponse<Tenant>>(
      `/tenants/admin/tenants/${id}/suspend`,
      {
        reason,
      }
    );
    return response.data.data!;
  },

  /**
   * Activate tenant (admin only)
   */
  activateTenant: async (id: string): Promise<Tenant> => {
    const response = await apiClient.post<ApiResponse<Tenant>>(
      `/tenants/admin/tenants/${id}/activate`
    );
    return response.data.data!;
  },

  /**
   * Check subdomain availability
   */
  checkSubdomain: async (
    subdomain: string
  ): Promise<{ available: boolean; reason?: string }> => {
    const response = await apiClient.get<
      ApiResponse<{ available: boolean; reason?: string }>
    >('/tenants/check-subdomain', {
      params: { subdomain },
    });
    return response.data.data!;
  },

  /**
   * Get users belonging to a specific tenant (admin only)
   */
  getTenantUsers: async (
    tenantId: string
  ): Promise<
    {
      id: string;
      email: string;
      first_name: string | null;
      last_name: string | null;
      roles: string[];
      is_active: boolean;
      is_verified: boolean;
      last_login_at: string | null;
      created_at: string;
    }[]
  > => {
    const response = await apiClient.get<
      ApiResponse<{
        users: any[];
        total: number;
      }>
    >('/users/admin/users', {
      params: { tenant_id: tenantId, per_page: 100 },
    });
    return response.data.data!.users;
  },

  /**
   * Get tenant statistics
   */
  getStats: async (): Promise<TenantStats> => {
    const response = await apiClient.get<ApiResponse<TenantStats>>(
      '/admin/tenants/stats'
    );
    return response.data.data!;
  },
};
